import { useEffect } from "react";

export const useGameConnectionListeners = (
  socket,
  {
    hasJoinedGameRef,
    nameRef,
    roomRef,
    showMessage,
    setLoggedIn,
    clearAllNotifications,
    notificationRefs,
    dismissingRef,
    setGameLog,
    setCurrentPhase,
    setStocks,
    setIndexes,
    setActiveEvents,
    setVisualEffects,
    setCurrentTurn,
    setPlayerCash,
    setPlayerNetWorths,
    setPlayerPortfolios,
    setPlayerIndexPortfolios,
    setActionsRemaining,
    setMaxActionsThisTurn,
    setStockOwnershipCounts,
    setStockOwnershipByPlayer,
    setIndexOwnershipByPlayer,
    setPlayerActionCards,
    setNewActionCardIds,
    setNewEventIds,
    setMyTurn,
    playerColorsRef,
    playerEmojisRef
  }
) => {
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const handleDisconnect = () => {
      if (hasJoinedGameRef.current) {
        showMessage("❌ Connection to server lost. Returning to home page...", () => {
          setLoggedIn(false);
        });
      } else {
        setLoggedIn(false);
      }
    };

    const handleConnectError = () => {
      if (hasJoinedGameRef.current) {
        showMessage("❌ Failed to connect to server. Returning to home page...", () => {
          setLoggedIn(false);
        });
      } else {
        setLoggedIn(false);
      }
    };

    const handleServerError = (errorDetails) => {
      console.error("[SERVER ERROR]", errorDetails);
      const errorMessage = errorDetails.message || "A server error occurred. Please refresh the page.";
      
      showMessage(`🚨 ${errorMessage}`, () => {
        if (errorDetails.debug) {
          console.error("[SERVER ERROR DEBUG]", errorDetails.debug);
        }
      }, false);
      
      if (errorDetails.debug) {
        console.error("[SERVER ERROR DETAILS]", {
          errorId: errorDetails.errorId,
          timestamp: errorDetails.timestamp,
          debug: errorDetails.debug
        });
      }
    };

    const handleShowMessage = (message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] show_message received');
      }
      showMessage(`📢 ${message}`);
    };

    const buildPlayerRows = (playerData) => {
      if (!playerData) return null;
      return Object.entries(playerData)
            .map(([playerName, info]) => ({
              name: playerName,
              position: info.position,
              netWorth: info.netWorth,
              cash: info.cash,
              stockCount: info.stockCount,
              biggestSell: info.biggestSell ?? 0,
              biggestGain: info.biggestGain ?? 0,
              color: playerColorsRef?.current ? playerColorsRef.current[playerName] : null,
              emoji: playerEmojisRef?.current ? playerEmojisRef.current[playerName] : null
            }))
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    };
      
    const buildShareMessage = (message, playerData, playerName) => {
        if (!playerData || !playerName || !playerData[playerName]) {
          return `${message}\n\n🎮 Pump N Dump - The Stock Market Strategy Game\nA strategic card game where players buy and sell stocks, play action cards, and navigate market events. Build your portfolio, manipulate prices, and become the wealthiest player!\n\n${window.location.origin}`;
        }

        const playerInfo = playerData[playerName];
        const position = playerInfo.position;
        const netWorth = playerInfo.netWorth;
        
      const positionEmojis = { 1: '🥇', 2: '🥈', 3: '🥉' };
      const positionTexts = { 1: '1st', 2: '2nd', 3: '3rd' };
        
        const emoji = positionEmojis[position] || '🎯';
        const positionText = positionTexts[position] || `${position}th`;
        
        let shareMessage = `I just finished a game of Pump N Dump ${emoji}\n\n`;
        shareMessage += `Finished in ${positionText} place with a net worth of $${netWorth}!\n\n`;
        shareMessage += `🎮 Pump N Dump - The Stock Market Strategy Game\n`;
        shareMessage += `A strategic card game where players buy and sell stocks, play action cards, and navigate market events. Build your portfolio, manipulate prices, and become the wealthiest player!\n\n`;
        shareMessage += `${window.location.origin}`;
        
        return shareMessage;
      };

    const handleEndGame = (data) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] end_game received');
      }
      clearAllNotifications();
      if (notificationRefs) notificationRefs.current = {};
      if (dismissingRef) dismissingRef.current.clear();
      
      const message = typeof data === 'string' ? data : data.message;
      const playerData = typeof data === 'object' && data.playerData ? data.playerData : null;
      const playerRows = buildPlayerRows(playerData);
      
      showMessage(message, () => {
        currentSocket.emit("leave_room", { name: nameRef.current, room: roomRef.current });
        setLoggedIn(false);
      }, {
        showShare: true,
        shareText: buildShareMessage(message, playerData, nameRef.current),
        playerData: playerData,
        endGameSummary: playerRows
      });
    };

    const handlePlayerQuit = (message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_quit received');
      }
      clearAllNotifications();
      if (notificationRefs) notificationRefs.current = {};
      if (dismissingRef) dismissingRef.current.clear();
      
      showMessage(message || "You have left the game", () => {
        currentSocket.emit("leave_room", { name: nameRef.current, room: roomRef.current });
        setLoggedIn(false);
      }, false);
    };

    const handleEndGameError = (message) => {
      showMessage(message || "End game is only available in development mode");
    };

    const handleGameReset = () => {
      clearAllNotifications();
      if (notificationRefs) notificationRefs.current = {};
      if (dismissingRef) dismissingRef.current.clear();
      
      setGameLog([]);
      setCurrentPhase(null);
      setStocks([]);
      setIndexes([]);
      setActiveEvents([]);
      setVisualEffects([]);
      setCurrentTurn("");
      setPlayerCash({});
      setPlayerNetWorths({});
      setPlayerPortfolios({});
      setPlayerIndexPortfolios({});
      setActionsRemaining({});
      setMaxActionsThisTurn({});
      setStockOwnershipCounts({});
      setStockOwnershipByPlayer({});
      setIndexOwnershipByPlayer({});
      setPlayerActionCards([]);
      setNewActionCardIds(new Set());
      setNewEventIds(new Set());
      setMyTurn(false);
    };

    const handlePlayerRejoined = ({ playerName, room, roundNumber, currentPhase, message }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_rejoined received', { playerName, room, roundNumber, currentPhase });
      }
      // Show welcome back message to the player
      showMessage(message || `Welcome back, ${playerName}!`);
    };

    currentSocket.on("disconnect", handleDisconnect);
    currentSocket.on("connect_error", handleConnectError);
    currentSocket.on("server_error", handleServerError);
    currentSocket.on("show_message", handleShowMessage);
    currentSocket.on("end_game", handleEndGame);
    currentSocket.on("player_quit", handlePlayerQuit);
    currentSocket.on("end_game_error", handleEndGameError);
    currentSocket.on("game_reset", handleGameReset);
    currentSocket.on("player_rejoined", handlePlayerRejoined);

    return () => {
      currentSocket.off("disconnect", handleDisconnect);
      currentSocket.off("connect_error", handleConnectError);
      currentSocket.off("server_error", handleServerError);
      currentSocket.off("show_message", handleShowMessage);
      currentSocket.off("end_game", handleEndGame);
      currentSocket.off("player_quit", handlePlayerQuit);
      currentSocket.off("end_game_error", handleEndGameError);
      currentSocket.off("game_reset", handleGameReset);
      currentSocket.off("player_rejoined", handlePlayerRejoined);
    };
  }, [socket, hasJoinedGameRef, nameRef, roomRef, showMessage, setLoggedIn, clearAllNotifications, notificationRefs, dismissingRef, setGameLog, setCurrentPhase, setStocks, setIndexes, setActiveEvents, setVisualEffects, setCurrentTurn, setPlayerCash, setPlayerNetWorths, setPlayerPortfolios, setPlayerIndexPortfolios, setActionsRemaining, setMaxActionsThisTurn, setStockOwnershipCounts, setStockOwnershipByPlayer, setIndexOwnershipByPlayer, setPlayerActionCards, setNewActionCardIds, setNewEventIds, setMyTurn]);
};

